/* eslint-disable react/forbid-prop-types,import/no-unresolved */
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Switch, Route, useRouteMatch, Link, useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { MAIN_ICON_COLORS } from 'assets/components';
import { isMobile } from 'react-device-detect';
import { useMutation } from 'react-query';
import Icon from '../../components/Icon';
import { ICONS } from '../../components/Icon/constants';
import { hideSupportWidget, setupSupportWidget } from '../../utilities/helpers';
import { resetUserSession } from '../../utilities/caching';
import { useSetting } from '../../utilities/sharedHooks/settings';
import PageTitle from '../../screens/UserSettings/partials/PageTitle';
import HelpCenterButton from '../../screens/UserSettings/partials/HelpCenterButton';
import PersonalInfo from '../../screens/UserSettings/PersonalInfo';
import DisplayCurrency from '../../screens/UserSettings/DisplayCurrency';
import { useUserPersonalInfo } from '../../utilities/sharedHooks/user';
import Security from '../../screens/UserSettings/Security';
import AboutUs from '../../screens/UserSettings/AboutUs';
import Account from '../../screens/UserSettings/Account';
import UpgradeAccount from '../../screens/UserSettings/Account/UpgradeAccount';
import { USER_LOGOUT } from '../../screens/UserSettings/mutations';
import { showAlert } from '../../utilities/notifications';
import { GenericLoading } from '../../components/Placeholders';

const DEBUG_LOG_ZENDESK_JWT_GENERATION =
  process.env.REACT_APP_DEBUG_LOG_ZENDESK_JWT_GENERATION === 'true';

const TabSwitch = ({ children }) => {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const { pathname } = useLocation();

  const { zendeskJWT } = useUserPersonalInfo();

  const [isCustomerSupportEnabled] = useSetting(
    'app_features',
    'feature_allow_in_app_customer_support'
  );

  useEffect(() => {
    if (zendeskJWT && isCustomerSupportEnabled) {
      setupSupportWidget(zendeskJWT, DEBUG_LOG_ZENDESK_JWT_GENERATION);
    }
  }, [zendeskJWT, isCustomerSupportEnabled]);

  const FULL_SCREENS_PATHS = useMemo(() => ['/dashboard/settings/upgrade-account'], []);

  const fullScreenRoutes = useMemo(
    () => FULL_SCREENS_PATHS.includes(pathname),
    [pathname, FULL_SCREENS_PATHS]
  );

  const [hoverIconsStatus, setHoverIconsStatus] = useReducer(
    (prev, next) => ({ ...prev, ...next }),
    {
      [ICONS.USER_CIRCLE]: false,
      [ICONS.USER_ACCOUNT]: false,
      [ICONS.SECURITY_SHIELD]: false,
      [ICONS.EXCHANGE]: false,
      [ICONS.SHOP]: false,
      [ICONS.LOGOUT]: false,
    }
  );

  const tabs = useMemo(
    () =>
      children
        .filter((c) => c !== false)
        .map((item) => ({
          path: item.props.path,
          label: item.props.label,
          description: item.props.description,
          descriptionSettings: item.props.descriptionSettings,
          icon: item.props.icon,
          isDisabled: item.props.isDisabled,
        })),
    [children]
  );

  const { mutate: logoutUser, isLoading: isLoadingLogout } = useMutation(USER_LOGOUT, {
    onSettled: (_, error) => {
      if (!error) {
        if (isCustomerSupportEnabled) {
          hideSupportWidget();
        }
        resetUserSession();
        history.push('/promo');
      } else {
        showAlert({ message: error.message, type: 'danger' });
      }
    },
  });

  const handleLogout = () => {
    logoutUser();
  };

  const [windowSize, setWindowSize] = useState([window.innerWidth, window.innerHeight]);

  useEffect(() => {
    const handleWindowResize = () => {
      setWindowSize([window.innerWidth, window.innerHeight]);
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (windowSize[0] > 720 && pathname.endsWith('settings')) {
      history.push(`${pathname}/profile`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, windowSize]);

  const renderTab = useCallback(
    (path, label, description, descriptionSettings, icon, index, maxItemNumbers) => (
      <div
        key={`${label}-${index}`}
        role="presentation"
        tabIndex={-1}
        onMouseEnter={() => setHoverIconsStatus({ [icon]: true })}
        onMouseLeave={() => setHoverIconsStatus({ [icon]: false })}>
        <Link
          key={path}
          to={path}
          className={`cursor-pointer settings-nav-item ${
            index !== maxItemNumbers - 1 ? 'mb-2' : ''
          } ${
            pathname.indexOf(path) === 0 ? 'settings-nav-item-active' : ''
          } d-flex flex-row justify-content-start align-items-center`}>
          <div className="me-3" style={{ height: '1.5rem', width: '1.5rem' }}>
            <Icon
              icon={icon}
              color={
                hoverIconsStatus[icon] && pathname.indexOf(path) !== 0
                  ? MAIN_ICON_COLORS.PRIMARY
                  : MAIN_ICON_COLORS.WHITE
              }
            />
          </div>
          <div className="d-flex flex-column">
            <p className="text-bold letter-spacing-2 text-f5">{translate(label)}</p>
            {description && (
              <p className="text-shadow text-f6 letter-spacing-3">{translate(description)}</p>
            )}
            {descriptionSettings && (
              <p className="text-shadow text-f6 letter-spacing-3">{descriptionSettings}</p>
            )}
          </div>
        </Link>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, hoverIconsStatus]
  );

  const renderLogoutBtn = () => (
    <div
      tabIndex={-1}
      role="presentation"
      onClick={isLoadingLogout ? () => {} : handleLogout}
      onMouseEnter={() => setHoverIconsStatus({ [ICONS.LOGOUT]: true })}
      onMouseLeave={() => setHoverIconsStatus({ [ICONS.LOGOUT]: false })}
      className={`cursor-pointer settings-nav-item ${
        isLoadingLogout ? 'bg-lighter' : ''
      } d-flex flex-row justify-content-start align-items-center py-4`}>
      {isLoadingLogout ? (
        <div className="w-100 d-flex justify-content-center align-items-center">
          <GenericLoading isFullScreen={false} customSize="1.5rem" customSpacing="" />
        </div>
      ) : (
        <>
          <div className="me-3" style={{ height: '1.5rem', width: '1.5rem' }}>
            <Icon
              icon={ICONS.LOGOUT}
              color={
                hoverIconsStatus[ICONS.LOGOUT] ? MAIN_ICON_COLORS.PRIMARY : MAIN_ICON_COLORS.WHITE
              }
            />
          </div>
          <div className="d-flex flex-column">
            <p className="text-f5 letter-spacing-2">
              <b>{translate('settings_logout_tab_label')}</b>
            </p>
          </div>
        </>
      )}
    </div>
  );

  if (fullScreenRoutes) {
    return <Switch>{children}</Switch>;
  }

  return (
    <div className="settings px-lg-0 px-md-0 px-3 mt-lg-5 mt-md-5 mt-sm-5 pt-md-3 pt-sm-5 pt-2 w-100">
      {pathname.endsWith('settings') && (
        <div className="d-lg-none d-md-none d-block">
          <div className="d-flex d-md-none mb-4">
            <PageTitle />
          </div>
          <HelpCenterButton />
        </div>
      )}

      <div className="row w-100 mx-0 d-lg-flex d-md-flex d-sm-flex d-none">
        <div className="col-12 px-0 d-lg-none d-md-none d-sm-flex">
          <Switch>{children}</Switch>
        </div>
        <div className="col-lg-4 col-md-4 col-sm-12 px-0">
          <div className="settings-nav-container px-0 mt-5 d-lg-none d-md-none d-sm-flex">
            <div className="col-6 px-0 border border-1 border-bottom-0 border-right-0 border-top-0 border-grey-lighter pe-2">
              {tabs
                ?.filter((item) => !item.isDisabled)
                ?.slice(0, 3)
                ?.map(({ path, label, description, descriptionSettings, icon, index }) =>
                  renderTab(path, label, description, descriptionSettings, icon, index, 3)
                )}
            </div>
            <div className="col-6 px-0 border-0">
              {tabs
                ?.filter((item) => !item.isDisabled)
                ?.slice(3)
                ?.map(({ path, label, description, descriptionSettings, icon, index }) =>
                  renderTab(path, label, description, descriptionSettings, icon, index, 3)
                )}
              {renderLogoutBtn()}
            </div>
          </div>

          <div className="settings-nav-container d-lg-block d-md-block d-sm-none">
            {tabs
              ?.filter((item) => !item.isDisabled)
              ?.map(({ path, label, description, descriptionSettings, icon, index }) =>
                renderTab(path, label, description, descriptionSettings, icon, index, 3)
              )}
            {renderLogoutBtn()}
          </div>
        </div>
        <div className="col-8 px-0 ps-4 d-lg-flex d-md-flex d-sm-none">
          <Switch>{children}</Switch>
        </div>
      </div>

      {pathname.endsWith('settings') && (
        <div className="d-lg-none d-md-none d-sm-none d-block">
          <p className="text-f6 text-shadow">{translate('settings')}</p>
          <div className="bg-lighter rounded-extra px-3 mt-3">
            {tabs
              ?.filter((item) => !item.isDisabled)
              ?.map(({ path, label, description, descriptionSettings, icon }) => (
                <Link key={path} to={path} className="cursor-pointer settings-nav-item-mobile">
                  <div className="d-flex flex-row justify-content-start align-items-center py-3">
                    <div className="me-3" style={{ height: '1.5rem', width: '1.5rem' }}>
                      <Icon icon={icon} color={MAIN_ICON_COLORS.WHITE} />
                    </div>
                    <div className="d-flex flex-column">
                      <p className="text-dark text-f5">
                        <p className="text-bold letter-spacing-2">{translate(label)}</p>
                      </p>
                      {description && (
                        <p className="text-shadow text-f6 letter-spacing-3">
                          {translate(description)}
                        </p>
                      )}
                      {descriptionSettings && (
                        <p className="text-shadow text-f6 letter-spacing-3">
                          {descriptionSettings}
                        </p>
                      )}
                    </div>
                    <div className="ms-auto">
                      <i
                        style={{ width: '1rem' }}
                        className="icon icon-arrow-next-navigation text-primary text-f4 cursor-pointer"
                      />
                    </div>
                  </div>
                </Link>
              ))}
          </div>
          <div className="mt-5">
            <button
              style={{ height: '3.5rem' }}
              className="pt-1 pb-2 px-4 mx-auto text-white btn border-grey w-100 letter-spacing-2"
              type="button"
              disabled={isLoadingLogout}
              loading={isLoadingLogout ? 'true' : 'false'}
              onClick={handleLogout}>
              {translate('other_tab_option4')}
            </button>
          </div>
        </div>
      )}
      {!pathname.endsWith('settings') && (
        <div
          className={`d-flex flex-column d-lg-none d-md-none d-sm-none ${
            isMobile ? 'hide-scroll' : ''
          }`}>
          <div
            className="cursor-pointer text-hover"
            onClick={() => history.push('/dashboard/settings')}
            role="presentation">
            <i className="icon icon-arrow_back-navigation text-f5 me-2" aria-label="Back" />
          </div>
          <Switch>{children}</Switch>
        </div>
      )}
    </div>
  );
};

TabSwitch.propTypes = {
  children: PropTypes.array.isRequired,
};

const UserRouter = () => {
  const { path } = useRouteMatch();
  const { t: translate, i18n } = useTranslation();

  const { currency } = useUserPersonalInfo();

  const [isWhitelistFunctionalityEnabled] = useSetting(
    'app_features',
    'feature_toggle_ledger_whitelist_functionality'
  );

  const currencyDescriptionSettings = useMemo(() => {
    if (!process.env.REACT_APP_FLAVOR) {
      return `${translate(`lang_name_${i18n.language}`)} - ${currency?.toUpperCase()}`;
    }
    return currency?.toUpperCase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, currency]);

  return (
    <TabSwitch>
      <Route
        path={`${path}/profile`}
        label="personal_information_title_label"
        description="personal_information_tab_desc"
        icon={ICONS.USER_CIRCLE}
        component={PersonalInfo}
      />
      <Route
        path={`${path}/account`}
        label="settings_account_tab_label"
        description="settings_account_tab_desc"
        icon={ICONS.USER_ACCOUNT}
        component={Account}
      />
      <Route isDisabled path={`${path}/upgrade-account`} component={UpgradeAccount} />
      <Route
        path={`${path}/security`}
        isDisabled={!isWhitelistFunctionalityEnabled}
        label="settings_security_tab_label"
        description="settings_security_tab_desc"
        icon={ICONS.SECURITY_SHIELD}
        component={Security}
      />
      <Route
        path={`${path}/language-currency`}
        label="settings_language_display_currency_tab_label"
        descriptionSettings={currencyDescriptionSettings}
        icon={ICONS.EXCHANGE}
        component={DisplayCurrency}
      />
      <Route
        path={`${path}/about`}
        label="settings_about_tab_label"
        description="settings_about_tab_desc"
        icon={ICONS.SHOP}
        component={AboutUs}
      />
    </TabSwitch>
  );
};

export default UserRouter;
